/**
 * Champion-to-Close engine — model, per-surface state machine, and gates.
 * See CHAMPION_TO_CLOSE.md for the full spec. Read-only on the championship
 * universe; all staging is local. Human gate on every surface (nothing
 * auto-finalizes).
 */

/* THE RING SURFACES.
 *
 * Brian: "bezel is the only front of the ring. it gives a bezel top and bezel
 * bottom. we dont need two there."
 *
 * He is describing the object. A ring has ONE face — the bezel — and splitting
 * it into "top" and "bottom" invented a second surface that does not exist,
 * which meant every build carried an extra render, an extra approval and an
 * extra chance for the two halves of one face to disagree with each other.
 *
 * Five surfaces, each a real part of a real ring.
 */
export const RING_SURFACES = [
  { key: 'bezel', label: 'Bezel — the face', focus: 'championship crest, year, sport & division mark' },
  { key: 'side_1', label: 'Side 1', focus: 'school name & mascot' },
  { key: 'side_2', label: 'Side 2', focus: 'season record & conference' },
  { key: 'palmside', label: 'Palmside', focus: 'personalization panel' },
  { key: 'core', label: 'Core / Inside', focus: 'engraving & signature' },
]

// Per-surface status: empty → rendering → proposed → approved (or → revising).
export const SURFACE_STATUS = { EMPTY: 'empty', RENDERING: 'rendering', PROPOSED: 'proposed', APPROVED: 'approved', REVISING: 'revising' }

export function initSurfaces() {
  return RING_SURFACES.map((s) => ({
    key: s.key,
    label: s.label,
    focus: s.focus,
    status: SURFACE_STATUS.EMPTY,
    version: 0,
    proof: null, // { image, prompt, model }
    note: '',        // a REVISION — a correction to the last render
    aiPrompt: '',    // standing ART DIRECTION — a fact about the account
    approvedAt: null,
  }))
}

/**
 * Engineered per-surface prompt from the dossier (client never free-texts).
 * A human `note` (from a Revise) is woven in as an explicit revision directive
 * so a revise re-generates ONLY this surface with the revised intent.
 */
export function surfacePrompt(surfaceKey, dossier, note, operatorPrompt) {
  const s = RING_SURFACES.find((x) => x.key === surfaceKey)
  const c = dossier?.championship || {}
  const champ = dossier?.champion || {}
  const colors = (champ.colors || []).map((x) => x.hex).filter(Boolean).join(' and ')
  const bits = [
    `Championship ring ${s?.label || surfaceKey} — ${s?.focus || ''}`,
    champ.school && `for ${champ.school}`,
    champ.mascot && `(${champ.mascot})`,
    c.sport && `${c.sport} ${c.season || ''}`.trim() + ' champions',
    colors && `in the team's colors ${colors}`,
    'photorealistic jewelry render, deep engraving detail, on a dark reflective background',
    /* THE OPERATOR'S OWN WORDS, LAST AND THEREFORE LOUDEST.
     *
     * Brian: "it would be great if it provided us a space to include an ai
     * prompt for each."
     *
     * The engineered layer above is what the RECORD knows; this is what the
     * person looking at the ring knows, and the person wins. Placed at the end
     * because later instructions dominate, and kept SEPARATE from the revision
     * note — "make the crest bigger" (a correction to what came back) and
     * "this school always uses a script D" (a standing fact about the account)
     * are different things and flattening them loses the second one on every
     * re-render. */
    operatorPrompt && operatorPrompt.trim() && `ART DIRECTION — ${operatorPrompt.trim()}`,
    note && note.trim() && `REVISION — ${note.trim()}`,
  ].filter(Boolean)
  return bits.join(', ') + '.'
}

/* ---- WHOSE MARK IS ON A PROOF, AND WHETHER IT MAY LEAVE THE BUILDING ----
 *
 * SR's standing rule: the artwork engine assembles real owned components and
 * never AI-generates deliverable artwork. The note on a drawn proof said "Not
 * for customer delivery", but it was only a display string. Nothing stopped an
 * approved drawn surface from riding into the staged campaign, or from being
 * counted as an attached surface in "The ring, already built" email.
 *
 * THE GATE IS ON THE PROOF, NOT ON THE REQUEST, AND IT FAILS CLOSED.
 *
 * `buildSurfaceSpec().markSource` only says whether we SENT an owned crest URL.
 * Sending it is not the same as the image carrying it, and on the deployed
 * generator it never does: sr-portal-backend `artwork.js#generateConcept`
 * (commit 70178c5, the commit /health reports live) builds a text prompt and
 * never reads `logoC`. So every image it returns has a model-drawn mark,
 * whatever we sent.
 *
 * A proof therefore counts as owned ONLY when the generator's RESPONSE says it
 * placed the owned file, with a value in OWNED_MARK_SOURCES. Anything else (no
 * field, an unknown value, a request-side "supplied", a local proposal, a
 * failed render) resolves to drawn-by-model. Today that means no generated
 * surface is attachable at all, which is the true state. The day the backend
 * composites the owned file and reports it, the gate opens with no change here.
 */
export const MARK_SOURCE = Object.freeze({
  DRAWN: 'drawn-by-model',
  OWNED_SUPPLIED: 'owned-crest-supplied',
  /* ⚠️ THE MIDDLE RUNG, ADDED 2026-09-18, AND IT EXISTS TO STOP A COERCION.
   *
   * Compositing the genuine owned crest onto a GPT-generated housing produces
   * something that is half true: the MARK IS REAL and placed, the RING IS NOT.
   * Both of the values that already existed would have misreported it. Calling
   * it `drawn-by-model` understates it, because the crest genuinely is the
   * owned file. Calling it `owned-crest-composited` would be a lie that
   * UNLOCKS SENDING, because that value is the one thing that makes a surface
   * attachable.
   *
   * So it gets its own name, spelled out rather than implied, and it is
   * deliberately NOT in OWNED_MARK_SOURCES. */
  OWNED_ON_GENERATED: 'owned-crest-on-generated-housing',
})

/**
 * Response-side values that mean the image carries the owned crest file ON AN
 * SR-OWNED HOUSING. This is the set that unlocks attaching, exporting and
 * sending, so it stays as small as it can possibly be.
 *
 * ⚠️ `owned-crest-on-generated-housing` IS NOT IN HERE AND MUST NEVER BE. The
 * crest on such an image is real; the ring under it came out of a model. A
 * customer shown one is being shown a piece SR does not make, which is the
 * exact failure the whole gate exists to prevent.
 */
export const OWNED_MARK_SOURCES = Object.freeze(['owned-crest-composited'])

const isOwnedMark = (v) => OWNED_MARK_SOURCES.includes(v)

/* -------------------------------------------------------------------- *
 * THE LADDER, IN THE WORDS A PERSON USES.
 *
 * Three rungs, and the difference between them decides whether something can
 * go to a customer. The UI must never make somebody translate a field value to
 * find that out, so the human sentence lives here beside the value it
 * describes and there is exactly one of each.
 * -------------------------------------------------------------------- */
/* ⚠️ `sendable` MEANS "MAY GO OUT", NOT "IS THE REAL THING", and after
 * 2026-09-19 all three rungs may go out. What separates them is what a customer
 * is actually being shown, which is why every rung still carries `what` and
 * `why` in plain words. The label is now the ONLY thing standing between a
 * concept and somebody presenting it as a licensed proof, so it has to be
 * exact: no rung below 3 may be described as the school's crest. */
export const MARK_LADDER = Object.freeze([
  {
    value: MARK_SOURCE.DRAWN,
    rung: 1,
    label: 'Concept',
    what: 'Drawn by the model. The mark is the model’s approximation of the school’s mark, not the school’s artwork, and the ring is drawn too.',
    sendable: true,
    why: 'Fine for showing a direction. Say it is a concept: it is not the school’s licensed mark and it is a screen image, not a print file.',
  },
  {
    value: MARK_SOURCE.OWNED_ON_GENERATED,
    rung: 2,
    label: 'Concept, real mark',
    what: 'The school’s genuine mark file, placed on a housing the model drew. The mark is real; the ring is not one SR makes.',
    sendable: true,
    why: 'The mark is exact, so this reads as finished. The ring is still a concept, so do not present it as a piece from the catalogue.',
  },
  {
    value: 'owned-crest-composited',
    rung: 3,
    label: 'Proof',
    what: 'The school’s genuine mark file, placed on a real SR housing render. Nothing in it is invented.',
    sendable: true,
    why: 'The only rung that is SR’s actual product with the actual mark. This is the one that can be called a proof.',
  },
])

const LADDER_BY_VALUE = Object.fromEntries(MARK_LADDER.map((r) => [r.value, r]))

/**
 * Which rung an asset is on, for a person. An unknown or missing value reads as
 * the LOWEST rung, never the highest: the gate fails closed here exactly as it
 * does in `isAttachableSurface`, so a forged or future value cannot talk its
 * way up the ladder by being unrecognised.
 */
export function markRung(markSource) {
  return LADDER_BY_VALUE[String(markSource || '')] || MARK_LADDER[0]
}

/** Propose the engineered spec for a surface (no image yet — render fills it). */
export function proposeProof(surfaceKey, dossier, version, note, operatorPrompt) {
  return {
    image: null,
    prompt: surfacePrompt(surfaceKey, dossier, note, operatorPrompt),
    model: `spec-v${version}`,
    // What this request will say about the crest. Recorded at the time of the
    // proposal, so a logo that arrives later cannot relabel an older proof.
    // Only a File Manager crest counts as owned; a fallback link does not.
    requestedMark: ownedCrestUrl(dossier) ? MARK_SOURCE.OWNED_SUPPLIED : MARK_SOURCE.DRAWN,
    // Nothing has come back yet, so nothing is owned yet.
    markSource: MARK_SOURCE.DRAWN,
  }
}

/**
 * May this surface be attached to, exported with, or sent in a campaign?
 *
 * ⚠️ THIS GATE OPENED ON 2026-09-19, BY A BUSINESS DECISION, AND THE REASONING
 * BELONGS NEXT TO THE CODE.
 *
 * Brian: *"I'm fine with drawn logos as it's the only way we get what looks
 * like accurate and realistic artwork vs drag and drop bs."*
 *
 * Until now a surface could only be sent if the generator reported it carried
 * the OWNED crest file, so every model-drawn asset was refused however anyone
 * felt about it. That was the right default while nobody had decided; it is not
 * a decision code gets to keep making once the person who owns the commercial
 * risk has made it. So APPROVAL is now the gate, and the mark source is
 * INFORMATION rather than a veto.
 *
 * ⚠️ WHAT DID NOT CHANGE, and must not:
 *   · the VALUES stay truthful. A model-drawn mark is still `drawn-by-model`.
 *     Nothing coerces upward into `owned-crest-composited`, `markRung()` still
 *     fails closed, and `applyGptImage()` still overwrites any claimed mark
 *     source with the drawn one.
 *   · the LADDER stays visible. Sketch, better sketch, proof are commercially
 *     different things that can look identical, and the label is the only thing
 *     stopping a concept being presented as a licensed proof.
 *   · a HUMAN still approves. Nothing auto-publishes; this reads `approved`.
 *
 * ⚠️ AND THE THING THIS COSTS, said plainly rather than buried: a drawn
 * approximation of a real school's mark IS NOT that school's licensed mark.
 * Brian has accepted that trade for concept work, which is his to accept. The
 * system's job is now to carry that fact with the asset, not to block it, which
 * is why nothing in the UI may describe a rung 1 or rung 2 asset as the
 * school's crest.
 */
export function isAttachableSurface(surface) {
  return !!surface && surface.status === SURFACE_STATUS.APPROVED
}

export const attachableSurfaces = (surfaces) => (surfaces || []).filter(isAttachableSurface)

/**
 * Approved surfaces that are NOT the real placed mark.
 *
 * Renamed in spirit on 2026-09-19: nothing is withheld any more, so this no
 * longer answers "what is the gate holding back". It answers "what is going out
 * that a customer could mistake for a licensed proof", which is the question
 * that still matters and the one the UI should be surfacing.
 */
export const withheldSurfaces = (surfaces) =>
  (surfaces || []).filter((s) => s.status === SURFACE_STATUS.APPROVED && !isOwnedMark(s.proof?.markSource))

/* ---- THE RENDER PATH: POST /api/artwork/render, the assembler -----------
 *
 * This panel used to call POST /api/artwork/generate with a free-text prompt,
 * the text-to-image path Brian rejected on 2026-07-27 after it produced a ring
 * reading "STATE CHAMIONS". It now posts a BUILD to the same assembler the
 * Studio uses, so the title and school travel as keyed type the backend's
 * locked layer asserts verbatim in every prompt, the beast prompt and the
 * Section 0 quality bar apply, and every render leaves a draft build record.
 *
 * ⚠️ WHAT THIS DOES NOT DO, measured against sr-portal-backend @70178c5:
 *
 *  1. IT IS NOT IMAGE-FREE. renderService.js#renderOneView posts the assembled
 *     text to OpenAI /v1/images/generations (gpt-image-1), one image per view,
 *     and spend.js lists this route beside /generate as a user of the same
 *     metered capability behind the same SR_ALLOW_IMAGE_SPEND gate. With the
 *     gate off, as it is today, NEITHER route returns an image. Each surface
 *     asks for exactly ONE view, so if the gate is ever opened this bills one
 *     image per surface, the same count /generate did. It moves the cost to a
 *     better prompt; it does not delete it.
 *
 *  2. THE CREST IS STILL NOT PLACED. The assembler serializes logoC through
 *     refText(), which reads name/source/description and never the url, and
 *     the image call is text-only. No compositor exists in the service. So a
 *     render is still a picture DRAWN FROM WORDS, the response carries no
 *     markSource, and isAttachableSurface stays false for every surface this
 *     produces. That gate opens only when the backend composites the owned
 *     file and says so.
 *
 *  3. THE ENGINE REFUSES A BUILD WITH NO TITLE OR NO CREST (validateBuild), so
 *     this panel checks both first and names what is missing rather than
 *     spending a round trip on a 400. /generate would draw a crest from the
 *     school's name; this path will not, which is correct and also means a
 *     record with no owned crest cannot render here until one is uploaded.
 * ------------------------------------------------------------------------ */

/**
 * Which engine view renders which ring surface. The engine's ring views are
 * top, left-shank, right-shank, palm and exploded (prompt-config.js#VIEWS).
 *
 * PALMSIDE AND CORE SHARE ONE VIEW, and that is the engine's limit rather than
 * a choice here: `palm` is framed "so the interior engraving is fully
 * readable", which is the core, and there is no separate palm-side framing.
 * The two surfaces still differ by their per-surface direction line.
 */
export const SURFACE_VIEW = Object.freeze({
  bezel: 'top',
  side_1: 'left-shank',
  side_2: 'right-shank',
  palmside: 'palm',
  core: 'palm',
})

/**
 * The dossier's crest, ONLY if it is a file SR hosts in HubSpot File Manager.
 * `useDossier` falls back to `logoFind().logo` when `find.owned` is false, so
 * `logoUrl` can be somebody else's server. That is not a crest the locked layer
 * may name as owned (owned-assets), so it resolves to '' here.
 */
export function ownedCrestUrl(dossier) {
  const url = String(dossier?.champion?.logoUrl || '')
  return /^https:\/\/[^/]*hubspotusercontent[^/]*\/hubfs\/\d+\//i.test(url) || /^https:\/\/[^/]+\/hubfs\/\d+\//i.test(url)
    ? url : ''
}

/** The engine-side title: the sourced title line, never composed. */
function titleLineOf(dossier) {
  return String(dossier?.championship?.story?.titleLine || dossier?.seasonStory?.facts?.titleLine || '').trim()
}

/**
 * Why the render path cannot run for this dossier, in words a rep can act on.
 * Mirrors the engine's own validateBuild (titleTop, logoC) plus the school,
 * which the keyed type needs. Empty means the build will assemble.
 */
export function renderBlockers(dossier) {
  const out = []
  if (!String(dossier?.champion?.school || '').trim()) {
    out.push('No recorded winner, so there is no school to key onto the ring.')
  }
  if (!titleLineOf(dossier)) {
    out.push('No sourced title line. The engine keys the title verbatim and will not invent one; resolve the record with its source to capture it.')
  }
  if (!ownedCrestUrl(dossier)) {
    out.push('No owned crest in HubSpot File Manager. Upload it in the Artwork Studio (step 1), then reopen this panel. The engine will not draw a crest from the school name.')
  }
  return out
}

/**
 * The POST /api/artwork/render body for ONE surface. Pure. A field the
 * records do not answer is OMITTED, never blanked, the same rule the Studio's
 * buildPayload.js enforces, so an empty record cannot reach the engine
 * reading as keyed truth.
 */
export function buildSurfaceRender(surfaceKey, dossier, note, operatorPrompt) {
  const s = RING_SURFACES.find((x) => x.key === surfaceKey)
  const champ = dossier?.champion || {}
  const c = dossier?.championship || {}
  const facts = dossier?.seasonStory?.facts || {}
  const school = String(champ.school || '').trim()
  const crest = ownedCrestUrl(dossier)
  const teamColor = (champ.colors || []).map((x) => x?.hex).filter(Boolean)

  const keyed = {}
  const title = titleLineOf(dossier)
  if (title) keyed.titleTop = title
  if (school) keyed.titleBottom = school
  const record = String(c.story?.record || facts.record || '').trim()
  if (record) keyed.record = record

  /* Free-text direction rides in `scripts`, which the assembler prints as its
   * PER-STEP DIRECTION layer, below the locked layer. The surface focus says
   * which part of the ring this proof is about; the operator's standing art
   * direction and a revision note follow it, as they did on /generate. */
  const label = s?.label || surfaceKey
  const scripts = {}
  if (s?.focus) scripts[`${label} focus`] = s.focus
  if (operatorPrompt && operatorPrompt.trim()) scripts[`${label} art direction`] = operatorPrompt.trim()
  if (note && note.trim()) scripts[`${label} revision`] = note.trim()

  const setting = {}
  if (c.sport) setting.sport = c.sport
  if (c.state) setting.region = c.state

  return {
    product: 'ring',
    style: 'championship',
    mode: 'render',
    views: [SURFACE_VIEW[surfaceKey] || 'top'],
    ...(Object.keys(setting).length ? { setting } : {}),
    ...(teamColor.length ? { components: { teamColor } } : {}),
    // LogoCPicker's exact ref shape, so the engine and the build record read
    // the same crest the Studio would send.
    ...(crest ? { logoC: { name: `${school} crest`, source: 'HubSpot-owned', url: crest } } : {}),
    keyed,
    scripts,
    ...(dossier?.championshipId ? { championshipId: String(dossier.championshipId) } : {}),
  }
}

const PENDING_PREFIX = 'render pending:'
const ABORTED_PREFIX = 'render_aborted:'

/**
 * Read a render response OR a polled build record into one surface's outcome.
 * Pure, so the states are asserted rather than buried in a component.
 *
 * The render route answers at once with renderPending:true and does the work
 * detached. When image spend is off, the detached run finishes by leaving
 * renderPending TRUE with a "render pending:" note, permanently. So pending is
 * only terminal when that note says why; otherwise it is still in flight and
 * the caller keeps polling.
 */
export function readSurfaceRender(res) {
  const rec = res?.record || res || {}
  const images = Array.isArray(rec.images) ? rec.images : (Array.isArray(res?.images) ? res.images : [])
  const prompts = Array.isArray(rec.prompts) ? rec.prompts : (Array.isArray(res?.prompts) ? res.prompts : [])
  const notes = Array.isArray(rec.notes) ? rec.notes.map(String) : []
  const img = images[0] || {}
  const image = img.hostedUrl || img.url || null
  const prompt = prompts[0]?.prompt || res?.assembledPrompt || ''
  const buildId = rec.buildId || res?.buildId || null
  const pendingNote = notes.find((n) => n.startsWith(PENDING_PREFIX))
  const aborted = notes.find((n) => n.startsWith(ABORTED_PREFIX))

  const base = { buildId, prompt, image, hosted: !!img.hostedUrl, model: img.model || 'artwork-engine · render' }
  if (aborted) return { ...base, terminal: true, outcome: 'failed', error: aborted.slice(ABORTED_PREFIX.length).trim() }
  if (img.error) return { ...base, terminal: true, outcome: 'failed', error: String(img.error) }
  if (image && rec.renderPending !== true) return { ...base, terminal: true, outcome: 'rendered' }
  if (rec.renderPending === true && pendingNote) {
    return { ...base, terminal: true, outcome: 'no-image', pendingReason: pendingNote.slice(PENDING_PREFIX.length).trim() }
  }
  if (rec.renderPending === false) return { ...base, terminal: true, outcome: 'no-image', pendingReason: 'The engine finished without an image for this view.' }
  return { ...base, terminal: false, outcome: 'rendering' }
}

/**
 * How a surface proof's mark must be described on screen. Three states, because
 * "we sent the owned file" and "the engine used it" are not the same claim and
 * collapsing them is how an AI-drawn mark ships as a customer's crest.
 *
 * Reads the PROOF (its `markSource` from the response and its `requestedMark`
 * from the request). A bare request spec is still accepted: its
 * "owned-crest-supplied" reads as sent-but-drawn, never as owned.
 */
export function markNote(proof) {
  const p = proof || {}
  // A GPT image is checked FIRST, so nothing about its request or a stray
  // field can make it read as owned: a chat drew it.
  if (p.source === GPT_SOURCE) {
    return 'Made in the GPT: the mark is drawn by the model, not the school\'s crest. The image is an owned copy in HubSpot File Manager, but the crest on it is not. Not for customer delivery: it cannot be attached to or sent in a campaign.'
  }
  if (isOwnedMark(p.markSource)) {
    return 'The generator reports it placed the owned crest file. Check the returned mark against it before this leaves the building.'
  }
  const sent = p.requestedMark === MARK_SOURCE.OWNED_SUPPLIED || p.markSource === MARK_SOURCE.OWNED_SUPPLIED
  if (sent) {
    return 'The owned crest was named in this request, but the engine does not place the file (it passes the crest to the image model as words), so this mark is still drawn by the model. Not for customer delivery: it cannot be attached to or sent in a campaign.'
  }
  return 'Concept proof: the mark is drawn by the model, not the school\'s crest. Not for customer delivery: it cannot be attached to or sent in a campaign.'
}

/* ---- Surface transitions (pure) ------------------------------------ */
/**
 * Move a surface into `rendering` while the real render is in flight. A revise
 * `note` (else the surface's stored note) is woven into the engineered prompt so
 * the re-render targets ONLY this surface with the revised intent.
 */
export function startRender(surface, dossier, note) {
  const version = surface.version + 1
  const effectiveNote = note != null ? note : surface.note
  // `aiPrompt` is the operator's standing art direction and PERSISTS across
  // renders — it describes the account, not the last picture.
  return { ...surface, status: SURFACE_STATUS.RENDERING, version, note: effectiveNote || '',
    proof: proposeProof(surface.key, dossier, version, effectiveNote, surface.aiPrompt) }
}
/** Apply a render result: image + prompt from the engine (proposed for review). */
export function applyRender(surface, result) {
  return { ...surface, status: SURFACE_STATUS.PROPOSED, proof: {
    image: result?.image || null,
    prompt: result?.prompt || surface.proof?.prompt || '',
    model: result?.model || 'artwork-engine',
    dryRun: !!result?.dryRun,
    // The draft build record the render left, and the engine's own words for
    // why no image came back. Both are shown, so "no image" is never a mystery.
    buildId: result?.buildId || null,
    pendingReason: result?.pendingReason || '',
    assembled: !!result?.assembled,
    requestedMark: surface.proof?.requestedMark || MARK_SOURCE.DRAWN,
    // Fail closed: only the response can make a mark owned.
    markSource: isOwnedMark(result?.markSource) ? result.markSource : MARK_SOURCE.DRAWN,
  } }
}
/* Mirrors GPT_BRIDGE.SOURCE in features/artwork/gptBridge/bridgeModel.js.
 * Kept as a literal so this model stays free of feature imports; the verify
 * suite asserts the two agree. */
const GPT_SOURCE = 'gpt-bridge'

/**
 * AN IMAGE THAT CAME BACK FROM THE GPT, attached to one surface.
 *
 * The file is owned (HubSpot File Manager); the MARK on it is not. So the
 * proof is PROPOSED for a person to review, markSource is drawn-by-model
 * unconditionally, and isAttachableSurface stays false whatever happens next.
 * The provenance travels on the proof so the card can say where it came from
 * and when, and a later reviewer can match it to the build-record note.
 */
export function applyGptImage(surface, img) {
  return { ...surface, status: SURFACE_STATUS.PROPOSED, proof: {
    ...(surface.proof || {}),
    image: img?.url || null,
    model: 'GPT bridge',
    dryRun: false,
    error: undefined,
    assembled: true,
    source: GPT_SOURCE,
    gpt: {
      at: img?.at || '',
      route: img?.route || '',
      fileId: img?.fileId || '',
      gptUrl: img?.gpt || '',
      recorded: !!img?.record && !img?.recordError,
      recordError: img?.recordError || '',
    },
    markSource: MARK_SOURCE.DRAWN,
  } }
}

/** Render failed / engine not live — keep the spec, label the fallback (not fake success). */
export function failRender(surface, reason) {
  return { ...surface, status: SURFACE_STATUS.PROPOSED, proof: { ...(surface.proof || {}), image: null, model: 'render-pending', error: reason || 'artwork engine unavailable', markSource: MARK_SOURCE.DRAWN } }
}
/** Local-only propose (no network) — used when there is no dossier/engine. */
export function generateSurface(surface, dossier) {
  const version = surface.version + 1
  return { ...surface, status: SURFACE_STATUS.PROPOSED, version,
    proof: proposeProof(surface.key, dossier, version, surface.note, surface.aiPrompt) }
}

/** Set the operator's standing art direction for one surface. */
export function setAiPrompt(surface, aiPrompt) {
  return { ...surface, aiPrompt: aiPrompt || '' }
}

/* ---- Pricing deep-link (real, live engine) ------------------------- */
/* The engine's own domain, live since 2026-08-31. The Railway URL still
 * resolves and is deliberately left in place, so anything already bookmarked
 * or pasted into a thread keeps working — but nothing in this app points at it. */
export const PRICING_ENGINE_URL = 'https://pricing.srchamp.com'
/** Deep-link the record into the live pricing engine, pre-filled via query. */
export function pricingDeepLink(dossier) {
  const champ = dossier?.champion || {}
  const c = dossier?.championship || {}
  const qs = new URLSearchParams({
    product: 'rings', source: 'champion-to-close',
    ref: String(dossier?.championshipId || ''),
    school: champ.school || '', sport: c.sport || '', level: c.level || '',
  })
  return `${PRICING_ENGINE_URL}/?${qs.toString()}`
}

/* ---- Outreach payload ---------------------------------------------- */
export function campaignPayload(dossier, surfaces) {
  return {
    championshipId: dossier?.championshipId,
    school: dossier?.champion?.school,
    sport: dossier?.championship?.sport,
    // ATTACHABLE, not merely approved. A drawn mark never crosses the network
    // as campaign art, whatever its approval state.
    surfaces: attachableSurfaces(surfaces).map((s) => ({ key: s.key, version: s.version })),
    source: 'champion-to-close',
  }
}
export function approveSurface(surface) {
  if (surface.status !== SURFACE_STATUS.PROPOSED) return surface
  return { ...surface, status: SURFACE_STATUS.APPROVED, approvedAt: Date.now() }
}
export function reviseSurface(surface, note) {
  // Re-opens for revision — approval is never permanent, never automatic.
  return { ...surface, status: SURFACE_STATUS.REVISING, note: note || surface.note, approvedAt: null }
}

/* ---- Gates --------------------------------------------------------- */
export const approvedCount = (surfaces) => surfaces.filter((s) => s.status === SURFACE_STATUS.APPROVED).length
export const allApproved = (surfaces) => surfaces.length > 0 && surfaces.every((s) => s.status === SURFACE_STATUS.APPROVED)

/* ---- Coach + season enrichment stubs (labelled) -------------------- */
export function stubCoach(championship) {
  // The record now carries the head coach the SOURCE named, captured with the
  // crown. Prefer it over the directory lookup and over a blank: a coach we
  // read off the championship recap is better evidence than one matched by
  // school name, and it is the person who signs the order.
  const named = championship?.story?.coach
  if (named) return { name: named, role: 'Head Coach', source: 'record', confidence: 0.9 }
  return { name: '', role: 'Head Coach', source: 'stub', confidence: 0 }
}

/**
 * The season story.
 *
 * THIS FUNCTION USED TO INVENT ONE. It returned:
 *
 *   "<school> <level> <sport> champions (<season>). Season story pending
 *    enrichment — headline record, key wins, and the title-clinching moment
 *    go here."
 *
 * That trailing sentence is TEMPLATE INSTRUCTION TEXT — a note to whoever was
 * going to write the real copy — and this panel composes outreach. It was one
 * click from being emailed to a coach. A placeholder that reads like prose is
 * far more dangerous than an empty field, because an empty field is obviously
 * unfinished and a placeholder is only obviously unfinished to the person who
 * wrote it.
 *
 * So there are now exactly two outcomes:
 *   · the REAL story the source stated, captured with the crown → `record`
 *   · nothing, with a reason → `missing`
 *
 * There is deliberately no third option that fabricates something in between.
 */
export function deriveSeasonStory(championship, champion) {
  const s = championship?.story || {}
  if (s.text) {
    return {
      text: s.text,
      source: 'record',
      sourceUrl: s.sourceUrl || '',
      // The engravable facts, kept structured rather than mashed into prose so
      // the ring builder and the email can each take what they need.
      facts: {
        titleLine: s.titleLine || '',
        score: s.score || '',
        runnerUp: s.runnerUp || '',
        record: s.record || '',
        venue: s.venue || '',
      },
    }
  }

  // No story on the record. Say so, name what is missing, and emit NOTHING
  // that could be mistaken for copy.
  const school = champion?.school || championship?.champion
  return {
    text: '',
    source: 'missing',
    reason: school
      ? 'No story captured from a source yet. Resolve this record with a source URL and the score, opponent, record and venue come with it.'
      : 'No champion on this record yet.',
  }
}

/**
 * WHAT THE GATE SAYS WHEN THERE IS NO WINNER ON THE RECORD.
 *
 * It used to say "a champion", which rendered as "Not sendable yet: missing a
 * champion". Brian read that as the championship selector being broken, and
 * the wording invited it: picking a championship only sets `championshipId`.
 * What the gate actually reads is `dossier.champion.school`, which comes from
 * the record's `current_champion` (falling back to `sr_winning_school`), and
 * nothing in this panel writes either one.
 *
 * So the label names the field and the control that fills it. Building a
 * champion entry control in this panel is deliberately NOT done here: it needs
 * a write path Brian wants to review on its own.
 */
export const MISSING_CHAMPION_LABEL =
  'a recorded winner (current_champion is blank on this record; set it with Submit for Win in the championship drawer)'

/**
 * THE SHORT FORM OF A MISSING-ITEM LABEL, for places with one line to spend.
 *
 * ⚠️ FIXING THE WORDING CREATED A SECOND PROBLEM IN THE SAME PLACE. The label
 * above is correct and it is 117 characters. The sequence row rendered
 * `needs {missing.join(' · ')}` on a single line, so the honest label came out
 * as a chip reading "needs a recorded winner (current_champion is blank on
 * this record; set it with Submit for Win in the championship drawer) · a
 * season story" — unreadable on a desktop row and hopeless at 375px. A label
 * nobody can read is not better than a label that misleads.
 *
 * So a label may carry a parenthetical that says HOW TO FIX IT. The chip shows
 * the part before it; the expanded panel shows the whole thing, one per line.
 * Neither place invents wording: both are the same string, cut or uncut.
 */
export function shortMissing(label) {
  const s = String(label || '')
  const cut = s.indexOf(' (')
  return (cut === -1 ? s : s.slice(0, cut)).trim()
}

/**
 * Is there enough real, sourced material to put in front of a customer?
 * The email composer asks this BEFORE it drafts, so nothing unfinished can be
 * sent by someone who did not read the panel closely.
 */
export function outreachReady(dossier) {
  const s = dossier?.seasonStory
  const missing = []
  if (!dossier?.champion?.school) missing.push(MISSING_CHAMPION_LABEL)
  /* A HAND-WRITTEN STORY COUNTS. The rule was never "only a source may speak"
   * — it was "nothing may be FABRICATED". A rep who was on the phone with the
   * coach is a better source than a page nobody has found, and refusing their
   * words left the gate stuck behind a fact somebody already had. What stays
   * banned is invented copy and placeholder prose. */
  if (!s || !s.text || (s.source !== 'record' && s.source !== 'manual')) {
    missing.push('a season story')
  }
  if (!dossier?.championship?.story?.titleLine) missing.push('the exact championship wording')
  return { ready: missing.length === 0, missing }
}
